/**
 * Normalize phone number to E.164 format (+1XXXXXXXXXX for US numbers)
 * Handles various input formats like (505) 862-9432, 505-862-9432, 5058629432, etc.
 * 
 * @param phoneNumber - Raw phone number input
 * @returns Normalized phone number in E.164 format
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Handle different cases
  if (digitsOnly.length === 10) {
    // US number without country code (e.g., 5058629432)
    return `+1${digitsOnly}`;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    // US number with country code (e.g., 15058629432)
    return `+${digitsOnly}`;
  } else if (digitsOnly.length === 11 && !digitsOnly.startsWith('1')) {
    // Assume it's a US number with a leading digit
    return `+1${digitsOnly}`;
  } else if (digitsOnly.length > 11) {
    // International number, assume it's missing the + sign
    return `+${digitsOnly}`;
  }
  
  // If we can't normalize it, return as-is (validation will catch invalid numbers)
  return phoneNumber;
}

/**
 * Validate if a phone number is in a reasonable format for sending OTP
 * 
 * @param phoneNumber - Normalized phone number
 * @returns true if the phone number looks valid
 */
export function isValidPhoneNumber(phoneNumber: string): boolean {
  // Check if it's in E.164 format and has a reasonable length
  const e164Regex = /^\+[1-9]\d{10,14}$/;
  return e164Regex.test(phoneNumber);
}
